import React, { FC, useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Company, Contract, ContractLightSearch, CreateApproval, Task } from "@/types";
import { archiveContract, postApproveContract, postArchiveContract, useGetContractById } from "@/services/Contracts";
import Spinner from "@/components/atoms/Spinner/Spinner";
import Text from "@/components/atoms/Text";
import { Group } from "@mantine/core";
import CTA from "@/components/atoms/CTA";
import styles from "./styles.module.css";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import DrawerTabs, { DrawerTab } from "@/components/atoms/DrawerTabs";
import ContractDetailsTab from "./TabPanels/ContractDetailsTab";
import { useGetCompanyById } from "@/services/Company";
import ContractAttachmentsTab from "./TabPanels/ContractAttachmentsTab";
import SubHeader from "@/components/atoms/accordions/DrawerAccordion/SubHeader";
import dayjs from "dayjs";
import { useModal } from "@/contexts/ModalContext";
import { useCreateContractWizardStore } from "@/stores/createContractWizardStore";
import { useAuth } from "@/contexts/AuthContext";
import { stripTime } from "@/utils/Dates";

interface Props {
  task: Task;
  contractId: number;
  type: "approve" | "archive" | "expire";
  onClose: () => void;
}

const ContractTask: FC<Props> = ({ task, contractId, onClose, type }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentCompany, user } = useAuth();
  const { data: contract, isLoading } = useGetContractById(contractId);
  const { data: contractCreator } = useGetCompanyById(contract?.createdByCompany as number, {
    enabled: contract?.createdByCompany === currentCompany?.companyId,
  });
  const open = useCreateContractWizardStore((s) => s.open);
  const setTaskId = useCreateContractWizardStore((s) => s.setTaskId);
  const { openModal } = useModal();

  const { mutate: mutateApproveContract, isPending: isApprovePending } = useMutation<
    void,
    unknown,
    void,
    { detailSnapshot?: Contract; listSnapshots: [readonly unknown[], ContractLightSearch | undefined][] }
  >({
    mutationFn: async () => {
      if (!user || !currentCompany) throw new Error("Missing user or company context");
      const approvalBody: CreateApproval = {
        approvalDate: stripTime(dayjs().toDate()),
        approvedBy: user.id,
        approvalCompany: currentCompany?.companyId,
        contractId,
      };
      await postApproveContract(contractId, approvalBody);
    },
    onMutate: async () => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: ["getContractById", contractId] }),
        queryClient.cancelQueries({ queryKey: ["getContractsLight"] }),
      ]);
      const detailSnapshot = queryClient.getQueryData<Contract>(["getContractById", contractId]);
      const listSnapshots = queryClient.getQueriesData<ContractLightSearch>({ queryKey: ["getContractsLight"] });
      const optimisticApproval = {
        id: -1,
        approvalDate: stripTime(dayjs().toDate()).toISOString(),
        approvedBy: user?.id as number,
        approvalCompany: currentCompany?.companyId as number,
        contractId,
      };
      queryClient.setQueryData<Contract>(["getContractById", contractId], (old) =>
        old ? { ...old, approvals: [...old.approvals, optimisticApproval] } : old
      );
      queryClient.setQueriesData<ContractLightSearch>({ queryKey: ["getContractsLight"] }, (old) => {
        if (!old) return old;
        return {
          ...old,
          contracts: old.contracts.map((c) =>
            c.id === contractId ? { ...c, approvals: [...c.approvals, optimisticApproval] } : c
          ),
        };
      });
      return { detailSnapshot, listSnapshots };
    },
    onError: (_err, _vars, context) => {
      if (context?.detailSnapshot) queryClient.setQueryData(["getContractById", contractId], context.detailSnapshot);
      context?.listSnapshots?.forEach(([queryKey, data]) => queryClient.setQueryData(queryKey, data));
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["getContractById", contractId] });
      queryClient.invalidateQueries({ queryKey: ["getContractsLight"] });
      queryClient.invalidateQueries({ queryKey: ["getOpenTasks", currentCompany?.companyId] });
    },
    onSuccess: () => {
      onClose();
    },
  });

  const { mutate: mutateDecideToArchiveContract, isPending: isDecideArchivePending } = useMutation<
    void,
    unknown,
    "ACCEPT" | "DECLINE"
  >({
    mutationFn: async (answer) => {
      await postArchiveContract(contractId, task.id, answer);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getContractById", contractId],
      });
      await queryClient.invalidateQueries({
        queryKey: ["getOpenTasks", currentCompany?.companyId],
      });
      onClose();
    },
    onError: async (err) => {
      console.error("Error deciding to archive contract", err);
    },
  });

  const { mutate: mutateArchiveContract, isPending: isArchivePending } = useMutation<void, unknown, number>({
    mutationFn: async (id) => {
      await archiveContract(id, task.id.toString());
    },
    onSuccess: async (_data) => {
      await queryClient.invalidateQueries({
        queryKey: ["getContractById", contractId],
      });
      await queryClient.invalidateQueries({
        queryKey: ["getOpenTasks", currentCompany?.companyId],
      });
      onClose();
    },
  });

  const contractInfo = useMemo(() => {
    if (!contract) return null;
    const creatorId = contract.createdByCompany as number;
    const buyer = contract.contractBuyers?.[0] as Company | undefined;
    const seller = contract.contractSellers?.[0] as Company | undefined;

    if (!buyer || !seller) return null;
    if (type === "approve" && contractCreator) {
      if (seller.id === creatorId) {
        return {
          type: "Sales" as const,
          creator: contractCreator,
          counterparty: buyer,
          description: `${contractCreator.companyName} ${t("tasks:approveContract.hasOpenedSalesContractWith")} ${buyer.companyName} ${t("tasks:thatNeedsApproval")}`,
        };
      }
      if (buyer.id === creatorId && contractCreator) {
        return {
          type: "Purchase" as const,
          creator: contractCreator,
          counterparty: seller,
          description: `${contractCreator.companyName} ${t("tasks:approveContract.hasOpenedPurchaseContractWith")} ${seller.companyName} ${t("tasks:thatNeedsApproval")}`,
        };
      }
    } else if (type === "expire") {
      if (seller.id === creatorId && contractCreator) {
        return {
          type: "Sales" as const,
          creator: contractCreator,
          counterparty: buyer,
          description: `${contractCreator.companyName} ${t("tasks:expiringContract.expiringSalesContractWith")} ${buyer.companyName} ${t("tasks:thatRequiresAction")}`,
        };
      }
      if (buyer.id === creatorId && contractCreator) {
        return {
          type: "Purchase" as const,
          creator: contractCreator,
          counterparty: seller,
          description: `${contractCreator.companyName} ${t("tasks:expiringContract.expiringPurchaseContractWith")} ${seller.companyName} ${t("tasks:thatRequiresAction")}`,
        };
      }
    } else if (type === "archive") {
      if (seller.id === creatorId) {
        return {
          type: "Sales" as const,
          creator: contractCreator,
          counterparty: buyer,
          description: `${task.counterParty} ${t("tasks:archiveContract.wishToArchiveSalesContractWith")} ${buyer.companyName} ${t("tasks:thatRequiresDecision")}`,
        };
      }
      if (buyer.id === creatorId) {
        return {
          type: "Purchase" as const,
          creator: contractCreator,
          counterparty: seller,
          description: `${task.counterParty} ${t("tasks:archiveContract.wishToArchivePurchaseContractWith")} ${seller.companyName} ${t("tasks:thatRequiresDecision")}`,
        };
      }
    }
    return null;
  }, [contract, contractCreator, type, t]);

  const needApprovalFromCounterPart = useMemo(() => {
    if (!contract || !currentCompany) return false;
    if (type === "archive" || type === "expire") return false;
    return contract.approvals?.some((approval) => approval.approvalCompany === currentCompany?.companyId) ?? false;
  }, [contract, currentCompany, type]);

  if (isLoading || !contract) return <Spinner loading={true} />;

  const handleSubmit = (answer: "ACCEPT" | "DECLINE" | null) => {
    if (type === "approve") {
      mutateApproveContract();
    } else if (type === "expire") {
      open(contract.id);
      setTaskId(task.id);
      onClose();
    } else if (answer !== null) {
      mutateDecideToArchiveContract(answer);
    }
  };

  const handleArchiveExpiringContract = () => {
    if (!contract?.id) return;
    openModal({
      title: t("tasks:expiringContract.archiveModalTitle"),
      content: t("tasks:expiringContract.archiveModalContent"),
      type: "confirmation",
      primaryAction: {
        text: t("actions:confirm"),
        intent: "primary",
        onClick: () => {
          mutateArchiveContract(contract.id);
        },
      },
      secondaryAction: {
        text: t("actions:cancel"),
        intent: "secondary",
        onClick: () => {
          // nothing extra
        },
      },
    });
  };

  return (
    <DrawerTabs defaultValue="commodityDetails" className="full flex col between h-100 py-10">
      <Group align="center" className={styles.taskHeader}>
        <SVGAsset name="approve-contract-dark" />
        <Text size="md" display="technical" tt="uppercase" strong={true}>
          {type === "approve" && t("enumerables:taskTypes.APPROVE_CONTRACT")}
          {type === "archive" && t("enumerables:taskTypes.ARCHIVE_CONTRACT")}
          {type === "expire" && t("enumerables:taskTypes.CONTRACT_EXPIRING")}
        </Text>
      </Group>

      <DrawerTabs.List>
        <DrawerTabs.Tab value="commodityDetails">
          <DrawerTab svgName="hamburger" label={t("tasks:contractDetails")} />
        </DrawerTabs.Tab>
        <DrawerTabs.Tab value="attachments">
          <DrawerTab svgName="document" label={t("tasks:attachments")} />
        </DrawerTabs.Tab>
      </DrawerTabs.List>

      {contractInfo && <SubHeader description={`${contractInfo.description} `} />}

      <DrawerTabs.Panel value="commodityDetails" className={styles.tabPanel}>
        <ContractDetailsTab contract={contract} />
      </DrawerTabs.Panel>
      <DrawerTabs.Panel value="attachments" className={styles.tabPanel}>
        <ContractAttachmentsTab contract={contract} />
      </DrawerTabs.Panel>

      <Group className={styles.tabActionGroup} p={15} gap={16} align="center" justify="flex-end">
        {type === "expire" && (
          <>
            <CTA
              data-testid="contract-task-expire-renew-btn"
              intent="primary"
              size="sm"
              fullWidth={false}
              type="button"
              onClick={() => handleSubmit("ACCEPT")}
              disabled={isArchivePending}
              loading={isArchivePending}
              aria-label={t("tasks:expiringContract.renew")}
            >
              {t("tasks:expiringContract.renew")}
            </CTA>

            <CTA
              data-testid="contract-task-expire-archive-btn"
              intent="primary"
              size="sm"
              fullWidth={false}
              type="button"
              onClick={handleArchiveExpiringContract}
              disabled={isArchivePending}
              loading={isArchivePending}
              aria-label={t("tasks:expiringContract.archive")}
            >
              {t("tasks:expiringContract.archive")}
            </CTA>
          </>
        )}

        {type === "archive" && (
          <>
            <CTA
              data-testid="contract-task-archive-accept-btn"
              intent="primary"
              size="sm"
              fullWidth={false}
              type="button"
              onClick={() => handleSubmit("ACCEPT")}
              disabled={isDecideArchivePending}
              loading={isDecideArchivePending}
            >
              {t("actions:accept")}
            </CTA>

            <CTA
              data-testid="contract-task-archive-deny-btn"
              intent="primary"
              size="sm"
              fullWidth={false}
              type="button"
              onClick={() => handleSubmit("DECLINE")}
              disabled={isDecideArchivePending}
              loading={isDecideArchivePending}
            >
              {t("actions:deny")}
            </CTA>
          </>
        )}

        {type === "approve" && (
          <CTA
            data-testid="contract-task-approve-btn"
            intent="primary"
            size="sm"
            fullWidth={false}
            type="button"
            onClick={() => handleSubmit(null)}
            disabled={needApprovalFromCounterPart || isApprovePending}
            loading={isApprovePending}
          >
            {needApprovalFromCounterPart ? t("contracts:needsCounterPartyApproval") : t("actions:approve")}
          </CTA>
        )}

        <CTA intent="secondary" size="sm" fullWidth={false} type="button" onClick={onClose}>
          {t("actions:close")}
        </CTA>
      </Group>
    </DrawerTabs>
  );
};

export default ContractTask;
